import { Link } from 'react-router-dom';
import type { Student } from '@/types';
import { formatDate, formatNumber, nextDueDate, type TuitionStatus } from '@/lib/datetime';
import { gradeGroupOf, type GradeGroup } from '@/lib/grade';

const STATUS_META: Record<TuitionStatus, { label: string; badge: string; icon: string }> = {
  paid: {
    label: '결제완료',
    badge: 'bg-secondary-100 text-secondary-900',
    icon: 'ri-checkbox-circle-line',
  },
  overdue: {
    label: '미납',
    badge: 'bg-accent-100 text-accent-800',
    icon: 'ri-error-warning-line',
  },
  upcoming: {
    label: '결제 예정',
    badge: 'bg-background-200 text-foreground-700',
    icon: 'ri-time-line',
  },
};

const GROUP_STYLE: Record<GradeGroup, { icon: string; chip: string }> = {
  초등학생: { icon: 'ri-seedling-line', chip: 'bg-primary-100 text-primary-700' },
  중학생: { icon: 'ri-book-open-line', chip: 'bg-secondary-100 text-secondary-900' },
  고등학생: { icon: 'ri-graduation-cap-line', chip: 'bg-accent-100 text-accent-800' },
  기타: { icon: 'ri-user-line', chip: 'bg-background-200 text-foreground-700' },
};

interface TuitionRowProps {
  student: Student;
  status: TuitionStatus;
  cycleDue: string | null;
  busy: boolean;
  onTogglePaid: (student: Student, paid: boolean) => void;
}

export default function TuitionRow({
  student,
  status,
  cycleDue,
  busy,
  onTogglePaid,
}: TuitionRowProps) {
  const group = gradeGroupOf(student.grade);
  const meta = STATUS_META[status];
  const nextDate = nextDueDate(student.tuition_due_day);

  return (
    <div
      className={`flex flex-col gap-4 rounded-xl border p-4 transition-colors md:flex-row md:items-center md:justify-between ${
        status === 'overdue'
          ? 'border-accent-200 bg-accent-50/60'
          : 'border-background-200 bg-background-50'
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${GROUP_STYLE[group].chip}`}
        >
          <i className={GROUP_STYLE[group].icon}></i>
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link
              to={`/students/${student.id}`}
              className="truncate text-sm font-semibold text-foreground-900 transition-colors hover:text-primary-700"
            >
              {student.name}
            </Link>
            <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-semibold text-foreground-600">
              {group}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-foreground-500">
            {student.school || '학교 미입력'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 md:w-auto">
        <div>
          <p className="text-[11px] text-foreground-500">교습비</p>
          <p className="mt-0.5 text-sm font-semibold text-foreground-900">
            {student.tuition_fee ? `${formatNumber(student.tuition_fee)}원` : '미입력'}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-foreground-500">결제일</p>
          <p className="mt-0.5 text-sm font-semibold text-foreground-900">
            {student.tuition_due_day ? `매월 ${student.tuition_due_day}일` : '미입력'}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-foreground-500">다음 결제일</p>
          <p className="mt-0.5 text-sm font-semibold text-foreground-900">{formatDate(nextDate)}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 md:justify-end">
        <span
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${meta.badge}`}
        >
          <i className={meta.icon}></i>
          {meta.label}
        </span>
        {status === 'paid' ? (
          <button
            type="button"
            onClick={() => onTogglePaid(student, false)}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-arrow-go-back-line"></i>
            )}
            취소
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onTogglePaid(student, true)}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-check-line"></i>
            )}
            결제완료
          </button>
        )}
      </div>
    </div>
  );
}